import React from "react";
import { buildErrorRecord, saveErrorRecord, errorRecordText } from "./errorRecord";

// App-wide React error boundary. A single render-time throw anywhere in the
// routed tree (e.g. `summary.split(".")` when a listing has no description)
// used to unmount the WHOLE app -> blank white screen, because there was no
// boundary. This catches it, keeps the app alive, and shows a recoverable
// fallback instead of a white page.
//
// `resetKey` (pass the current route path) clears the error when the user
// navigates, so a crash on one page doesn't stick after moving away.
//
// The fallback also keeps what failed (./errorRecord.js): a reference on the
// screen and the details ready to copy, so a reported crash can be traced.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, record: null, copied: false, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface it loudly in the console (and CI/QC logs) so a caught crash is
    // never invisible — a swallowed error is as bad as the white screen.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] render crash:", error, info && info.componentStack);
    // Keep it for this tab and show its reference: the console is gone with the tab.
    const path = typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "";
    const record = buildErrorRecord(error, info, { path });
    saveErrorRecord(record);
    this.setState({ record, copied: false, showDetails: false });
  }

  copyDetails = () => {
    const text = errorRecordText(this.state.record);
    const clipboard = typeof navigator !== "undefined" ? navigator.clipboard : null;
    if (clipboard && typeof clipboard.writeText === "function") {
      Promise.resolve(clipboard.writeText(text))
        .then(() => this.setState({ copied: true }))
        .catch(() => this.setState({ showDetails: true }));
    } else {
      // No clipboard here (an old browser, or a page not served over https): show the text.
      this.setState({ showDetails: true });
    }
  };

  componentDidUpdate(prevProps) {
    // Auto-recover on navigation: when the reset key changes after an error,
    // drop the fallback so the next page renders normally.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, record: null, copied: false, showDetails: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          fontFamily: "Arial, sans-serif",
          color: "#212529",
        }}
      >
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <h2 style={{ margin: "0 0 8px", fontSize: 22 }}>Something went wrong on this page</h2>
          <p style={{ margin: "0 0 20px", color: "#6c757d", fontSize: 15, lineHeight: 1.5 }}>
            This page couldn&apos;t be displayed. You can go back to your search or reload the page.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <a
              href="/"
              style={{
                background: "#0b5cff",
                color: "#fff",
                padding: "10px 18px",
                borderRadius: 8,
                textDecoration: "none",
                fontSize: 14,
              }}
            >
              Back to search
            </a>
            <span
              role="button"
              tabIndex={0}
              onClick={() => window.location.reload()}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && window.location.reload()}
              style={{
                background: "#f1f3f5",
                color: "#212529",
                padding: "10px 18px",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 14,
                display: "inline-block",
              }}
            >
              Reload page
            </span>
          </div>
          {this.state.record && (
            <div style={{ marginTop: 22, color: "#6c757d", fontSize: 13, lineHeight: 1.5 }}>
              <span>Reference {this.state.record.ref}</span>
              <span aria-hidden="true"> · </span>
              {this.state.copied ? (
                <span role="status">Copied</span>
              ) : (
                <button
                  type="button"
                  onClick={this.copyDetails}
                  style={{ background: "none", border: 0, padding: 0, color: "#0b5cff", cursor: "pointer", fontSize: 13, textDecoration: "underline" }}
                >
                  Copy details
                </button>
              )}
              <div>Send this with your report so the cause can be found.</div>
              {this.state.showDetails && (
                <textarea
                  readOnly
                  aria-label="Error details"
                  value={errorRecordText(this.state.record)}
                  onFocus={(e) => e.target.select()}
                  style={{ marginTop: 10, width: "100%", height: 130, fontSize: 12, fontFamily: "monospace", color: "#212529" }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
